import React, { useState, useEffect } from 'react';
import { CardBitService } from '../../services/CardBitService';
import './CardBitTool.css';

// Expected bit lengths for known formats — used for mismatch warning
const KNOWN_FORMAT_BITS = {
  H10301:        26,
  H10302:        37,
  H10304:        37,
  Corporate1000: 35,
  HID34:         34,
};

const CardBitTool = () => {
  const [cardBits, setCardBits] = useState('');
  const [inputMode, setInputMode] = useState('binary'); // 'binary' | 'decimal' | 'hex'
  const [formats, setFormats] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadFormats();
  }, []);

  const loadFormats = async () => {
    try {
      const response = await CardBitService.getFormats();
      setFormats(response.formats || []);
    } catch (err) {
      setError('Failed to load card formats');
      console.error(err);
    }
  };

  // Convert input to binary string based on input mode
  const toBinaryBits = (input) => {
    if (inputMode === 'decimal') {
      const num = parseInt(input.trim(), 10);
      if (isNaN(num)) throw new Error('Invalid decimal number');
      return num.toString(2);
    }
    if (inputMode === 'hex') {
      const clean = input.trim().replace(/^0x/i, '');
      if (!/^[0-9a-fA-F]+$/.test(clean)) throw new Error('Invalid hex value — use characters 0-9 and A-F');
      const binary = parseInt(clean, 16).toString(2);
      const expectedLength = clean.length * 4; // Each hex char = 4 bits
      return binary.padStart(expectedLength, '0'); // Preserve leading zeros
    }
    return input.trim();
  };

  // Live bit-length mismatch warning (no extra state needed)
  const getBitLengthWarning = () => {
    if (!cardBits || !selectedFormat) return null;
    try {
      const bits = toBinaryBits(cardBits);
      const expected = KNOWN_FORMAT_BITS[selectedFormat];
      if (expected && bits.length !== expected) {
        return `⚠️ Format mismatch: "${selectedFormat}" expects ${expected} bits, but your input converts to ${bits.length} bits. Select the correct format or use Auto-detect.`;
      }
    } catch (e) { /* invalid input, ignore */ }
    return null;
  };

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    try {
      const bits = toBinaryBits(cardBits);
      const response = await CardBitService.analyzeCard(bits, selectedFormat);
      setAnalysis(response.analysis);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
  };

  const uploadFileAndAnalyze = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const text = (await file.text()).trim();
      setCardBits(text);
      let bits;
      if (inputMode === 'decimal') {
        bits = parseInt(text, 10).toString(2);
      } else if (inputMode === 'hex') {
        const clean = text.replace(/^0x/i, '');
        const binary = parseInt(clean, 16).toString(2);
        bits = binary.padStart(clean.length * 4, '0'); // Preserve leading zeros
      } else {
        bits = text;
      }
      const response = await CardBitService.analyzeCard(bits, selectedFormat);
      setAnalysis(response.analysis);
    } catch (err) {
      console.error('Upload/Analyze error', err);
      setError(err.message || 'Upload/Analyze failed');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateConfig = async () => {
    try {
      const response = await CardBitService.generateConfig(analysis);
      // Download as JSON (KCEACS reader_conf format)
      const content = typeof response.config === 'string'
        ? response.config
        : JSON.stringify(response.config, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kceacs_reader_conf_${Date.now()}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to generate KCEACS config');
    }
  };

  return (
    <div className="card-bit-tool">
      <div className="tool-header">
        <h2>Card Bit Analysis Tool</h2>
        <p>Analyze Wiegand card data and generate KCEACS reader configuration files</p>
      </div>

      <div className="tool-layout">
        {/* ── Main tool panel ── */}
        <div className="tool-main">
          <div className="tool-content">
            <div className="input-section">

              {/* Input mode toggle */}
              <div>
                <label>Input Type</label>
                <div className="mode-toggle">
                  <button
                    className={inputMode === 'binary' ? 'mode-btn active' : 'mode-btn'}
                    onClick={() => { setInputMode('binary'); setCardBits(''); }}
                  >Binary bits</button>
                  <button
                    className={inputMode === 'decimal' ? 'mode-btn active' : 'mode-btn'}
                    onClick={() => { setInputMode('decimal'); setCardBits(''); }}
                  >Decimal number</button>
                  <button
                    className={inputMode === 'hex' ? 'mode-btn active' : 'mode-btn'}
                    onClick={() => { setInputMode('hex'); setCardBits(''); }}
                  >Hex</button>
                </div>
              </div>

              <div>
                <label>
                  {inputMode === 'binary' ? 'Card Bit Data (Wiegand binary string)'
                    : inputMode === 'decimal' ? 'Card Data (decimal integer)'
                    : 'Card Data (hexadecimal)'}
                </label>
                {inputMode === 'binary' ? (
                  <textarea
                    value={cardBits}
                    onChange={(e) => setCardBits(e.target.value)}
                    placeholder="e.g. 00101100110101010101010101"
                    rows={4}
                  />
                ) : inputMode === 'decimal' ? (
                  <input
                    type="number"
                    className="decimal-input"
                    value={cardBits}
                    onChange={(e) => setCardBits(e.target.value)}
                    placeholder="e.g. 11072613"
                  />
                ) : (
                  <input
                    type="text"
                    className="decimal-input"
                    value={cardBits}
                    onChange={(e) => setCardBits(e.target.value)}
                    placeholder="e.g. A5F3C2 or 0xA5F3C2"
                    spellCheck={false}
                  />
                )}
              </div>

              {/* Bit-length mismatch warning */}
              {getBitLengthWarning() && (
                <div className="bit-warning">{getBitLengthWarning()}</div>
              )}

              <div>
                <label>Select Card Format <span className="label-hint">(leave blank to auto-detect)</span></label>
                <select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                >
                  <option value="">-- Auto-detect --</option>
                  {formats.map(format => (
                    <option key={format} value={format}>{format}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleAnalyze}
                disabled={!cardBits || loading}
                className="btn-primary"
              >
                {loading ? 'Analyzing...' : 'Analyze Card'}
              </button>

              <div className="upload-row">
                <div>
                  <label>Upload bit data file</label>
                  <p className="upload-hint">Plain <code>.txt</code> file containing one value matching the selected input type — a raw binary string (e.g. <code>00101100...</code>), a decimal integer, or a hex string (e.g. <code>A5F3C2</code>). One value per file.</p>
                </div>
                <div className="upload-controls">
                  <input type="file" accept=".txt" onChange={handleFileChange} />
                  <button
                    onClick={uploadFileAndAnalyze}
                    disabled={!file || uploading}
                    className="btn-secondary"
                  >
                    {uploading ? 'Uploading...' : 'Upload & Analyze'}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
            )}

            {analysis && (
              <div className="results-section">
                <h3>Analysis Results</h3>

                {!analysis.valid && (
                  <div className="warning-message">
                    ⚠️ Warning: Card data may be invalid or in an unknown format
                  </div>
                )}
            
            <div className="results-grid">
                  <div className="result-item">
                    <label>Facility Code:</label>
                    <span>
                      {analysis.facilityCode && analysis.facilityCode !== 'N/A' && analysis.facilityCode !== 'Unknown'
                        ? `0x${parseInt(analysis.facilityCode, 10).toString(16).toUpperCase()} (${analysis.facilityCode})`
                        : analysis.facilityCode || 'N/A'}
                    </span>
                  </div>
                  <div className="result-item">
                    <label>Card Number:</label>
                    <span>
                      {analysis.cardNumber && analysis.cardNumber !== 'N/A' && analysis.cardNumber !== 'Unknown'
                        ? `0x${parseInt(analysis.cardNumber, 10).toString(16).toUpperCase()} (${analysis.cardNumber})`
                        : analysis.cardNumber || 'N/A'}
                    </span>
                  </div>
                  <div className="result-item">
                    <label>Format:</label>
                <span>{analysis.format || 'Unknown'}</span>
              </div>
              <div className="result-item">
                <label>Bit Length:</label>
                <span>{analysis.bitLength || 'N/A'}</span>
              </div>
            </div>

                <div className="raw-data">
                  <h4>Raw Data</h4>
                  <pre>{JSON.stringify(analysis, null, 2)}</pre>
                </div>

              <button
                onClick={handleGenerateConfig}
                className="btn-secondary"
              >
                Download KCEACS Config (reader_conf.json)
              </button>
            </div>
          )}
          </div>
        </div>

        {/* ── Sidebar instructions ── */}
        <aside className="tool-sidebar">
          <h3>How to use</h3>

          <div className="sidebar-step">
            <span className="step-num">1</span>
            <div>
              <strong>Choose input type</strong>
              <p>Select <em>Binary bits</em> if you have the raw Wiegand bit string from a card reader capture (e.g. <code>00101100110101010101010101</code>).<br/>Select <em>Decimal number</em> if you have the card data as a decimal integer (e.g. <code>11072613</code>) — the tool converts it to binary automatically.<br/>Select <em>Hex</em> if you have the card data in hexadecimal format (e.g. <code>A5F3C2</code>).</p>
            </div>
          </div>

          <div className="sidebar-step">
            <span className="step-num">2</span>
            <div>
              <strong>Select card format</strong>
              <p>Choose the Wiegand format used by your card system (e.g. <em>H10301</em> for standard 26-bit HID, <em>H10302</em> for 37-bit HID). Leave on <em>Auto-detect</em> if you are unsure — the tool will match the bit length.</p>
            </div>
          </div>

          <div className="sidebar-step">
            <span className="step-num">3</span>
            <div>
              <strong>Analyze Card</strong>
              <p>Click <em>Analyze Card</em> to decode the data. The result shows the <strong>Facility Code</strong> (site identifier), <strong>Card Number</strong>, matched format, and parity validity.</p>
            </div>
          </div>

          <div className="sidebar-step">
            <span className="step-num">4</span>
            <div>
              <strong>Upload a file (optional)</strong>
              <p>You can upload a <code>.txt</code> file containing a single raw binary string or decimal number instead of typing it manually.</p>
            </div>
          </div>

          <div className="sidebar-step">
            <span className="step-num">5</span>
            <div>
              <strong>Generate KCEACS Config</strong>
              <p>After a successful analysis, click <em>Download KCEACS Config</em> to get a <code>reader_conf.json</code> file. This file can be loaded directly into the KCEACS reader configuration to tell the reader how to parse the card bits (parity positions, facility code offset, card number offset).</p>
            </div>
          </div>

          <div className="sidebar-formats">
            <h4>Common HID formats</h4>
            <table>
              <thead><tr><th>Format</th><th>Bits</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td>H10301</td><td>26</td><td>Standard HID 26-bit (most common)</td></tr>
                <tr><td>H10302</td><td>37</td><td>HID 37-bit with facility code</td></tr>
                <tr><td>H10304</td><td>37</td><td>HID 37-bit without facility code</td></tr>
                <tr><td>Corporate 1000</td><td>35</td><td>HID Corporate 1000</td></tr>
              </tbody>
            </table>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CardBitTool;